import { FC, useMemo, useState } from 'react';

import { BottomPopup } from '@shared/components/BottomPopup';
import { Icon } from '@shared/components/icons';
import { checkIOS } from '@shared/utils/miscUtil';
import { giftRecipientBaseUrl } from '@shared/utils/giftRecipientLink.util';
import { RWebShare } from 'react-web-share';

import { ShareChannel } from '../GiftCouponShareModal/GiftCouponShareModal';
import KakaoShareGiftButton from '../GiftCouponShareModal/KakaoShareGiftButton';

const SIMPLE_MESSAGE = '감사한 마음을 담아 작은 정성을 전해드립니다.';

interface Props {
  openId: string;
  shareKey: number;
  courtesyContent: string;
  linkUrl: string;
  seq?: number | null;
  onShare?: (channel: ShareChannel) => void;
  deliveryId?: string;
  deliveryCode?: string;
}

export const GiftDeliveryShareModal: FC<Props> = function GiftDeliveryShareModal({
  openId,
  shareKey,
  courtesyContent,
  linkUrl,
  seq,
  onShare,
  deliveryId,
  deliveryCode,
}) {
  const { ShareMessage, OtherApps } = Icon();
  const [simpleMode, setSimpleMode] = useState(false);

  const message = simpleMode ? SIMPLE_MESSAGE : courtesyContent;

  const smsText = useMemo(
    () => `${message}\n\n▼ 답례품 받기 (배송지 입력) ▼\n${linkUrl}`,
    [message, linkUrl],
  );
  const smsContent = smsText.replace(/\n/g, '%0D%0A');

  const serverCallbackArgs = useMemo(() => {
    if (!deliveryId) return undefined;
    const args: Record<string, string> = {
      shareContent: 'GiftDelivery',
      deliveryId,
      // 공유 링크(linkUrl)가 실제 가리키는 도메인을 기록 (수신자 전용 도메인 우선)
      domainUrl:
        giftRecipientBaseUrl ||
        process.env.REACT_APP_WEB_BASE_URL ||
        window.location.origin,
    };
    if (deliveryCode) args.deliveryCode = deliveryCode;
    return args;
  }, [deliveryId, deliveryCode]);

  return (
    <BottomPopup openId={openId}>
      <div className="w-screen rounded-t-2xl bg-white px-9 pb-12 pt-6">
        <h2 className="text-center text-lg font-bold text-gray-800">
          {seq ? (
            <>
              <span className="text-purple-600">#{seq}번째</span> 답례품 전달하기
            </>
          ) : (
            '답례품 전달하기'
          )}
        </h2>
        <p className="mt-1 text-center text-xs text-gray-500">
          받는 분이 직접 배송지를 입력합니다
        </p>

        {/* 모드 선택 탭 */}
        <div className="mt-4 flex rounded-lg bg-gray-100 p-1">
          <button
            onClick={() => setSimpleMode(false)}
            className={`flex-1 rounded-md py-2 text-sm font-medium transition ${
              !simpleMode ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500'
            }`}
          >
            답례글과 함께
          </button>
          <button
            onClick={() => setSimpleMode(true)}
            className={`flex-1 rounded-md py-2 text-sm font-medium transition ${
              simpleMode ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500'
            }`}
          >
            답례품만
          </button>
        </div>

        <div className="flex justify-center gap-6 pt-5">
          <a
            href={checkIOS() ? `sms:&body=${smsContent}` : `sms:?body=${smsContent}`}
            onClick={() => onShare?.(ShareChannel.SMS)}
          >
            <div className="flex h-[70px] w-[80px] flex-col items-center justify-center gap-2">
              <ShareMessage className="h-full w-full" />
              <p className="text-sm font-bold text-gray-700">문자</p>
            </div>
          </a>

          <div
            className="flex h-[70px] w-[80px] flex-col items-center justify-center gap-2"
            onClickCapture={() => onShare?.(ShareChannel.KakaoTalk)}
          >
            <KakaoShareGiftButton
              key={`${shareKey}-${simpleMode}`}
              content={message}
              linkUrl={linkUrl}
              buttonTitle="배송지 입력하기"
              serverCallbackArgs={serverCallbackArgs}
            />
            <p className="text-sm font-bold text-gray-700">카카오톡</p>
          </div>

          <RWebShare
            data={{
              text: smsText,
              url: linkUrl,
              title: '답례품',
            }}
            onClick={() => onShare?.(ShareChannel.Other)}
          >
            <div className="flex h-[70px] w-[80px] flex-col items-center justify-center gap-2">
              <OtherApps />
              <p className="text-sm font-bold text-gray-700">다른 앱</p>
            </div>
          </RWebShare>
        </div>
      </div>
    </BottomPopup>
  );
};
